import { useState } from 'react';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { TextField } from '../../components/TextField';
import MenuList from '@mui/material/MenuList';
import { MenuItem } from '../../components/Menu';
import { Button } from '../../components/Button';
import Popover from '@mui/material/Popover';
import { IconButton } from '../../components/IconButton';
import { Tooltip } from '../../components/Tooltip';
import CloseIcon from '@mui/icons-material/Close';

import { Account } from '../session/session-decode';
import { NAVBAR_ICON_SIZE } from './navbar-icon-size';

export interface AccountSelectorProps {
  showAccountSelector: boolean;
  accounts: Account[];
  filter: string;
  setFilter: (v: string) => void;
  accountName: string | null;
  onSelectAccount: (account: Account) => void;
  onLeaveAccount: () => void;
}

/**
 * Apresentação pura da troca de conta: recebe estado e handlers do `useNavbar` e
 * não decide nada — `accounts` já chega filtrada/ordenada e `showAccountSelector`
 * já chega calculado pelo núcleo puro.
 *
 * O nome da conta ativa fica à esquerda do gatilho (ícone de condomínio), com um
 * botão "X" entre eles para sair da conta selecionada quando há uma. O gatilho
 * abre a lista de troca; o cabeçalho do popover repete o nome da conta atual.
 *
 * A lista abre sob demanda num Popover ancorado ao gatilho: o Popover renderiza
 * via portal no `document.body`, então a listagem escapa do overflow/stacking da
 * AppBar em vez de ficar presa dentro da navbar.
 *
 * TextField + MenuList (e não Autocomplete): a lista já vem pré-filtrada pelo
 * hook via `filter`, e selecionar uma conta dispara a troca de tenant na hora
 * (não preenche o campo com o valor escolhido, como um Autocomplete faria). Um
 * campo de busca seguido de uma lista de ações imediatas espelha melhor o menu
 * suspenso do Angular do que um combobox de valor único.
 */
export function AccountSelector({
  showAccountSelector,
  accounts,
  filter,
  setFilter,
  accountName,
  onSelectAccount,
  onLeaveAccount,
}: AccountSelectorProps): JSX.Element {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);

  function handleOpen(event: MouseEvent<HTMLElement>): void {
    setAnchorEl(event.currentTarget);
  }

  function handleClose(): void {
    setAnchorEl(null);
  }

  function handleSelect(account: Account): void {
    onSelectAccount(account);
    handleClose();
  }

  function handleLeave(): void {
    onLeaveAccount();
    handleClose();
  }

  // Sem seleção de conta: só o rótulo do tenant atual, sem gatilho/popover.
  // Sem o gatilho, é o texto que encosta no divisor da faixa, então o afastamento de
  // 8px que viria do padding do IconButton passa a ser dele. Fica no texto, e não no
  // Box, para não sobrar espaço morto antes do divisor quando não há nome de conta.
  if (!showAccountSelector) {
    return (
      <Box>
        {accountName !== null && (
          <Typography variant="body2" sx={{ pr: 1 }}>
            {accountName}
          </Typography>
        )}
      </Box>
    );
  }

  const triggerTitle = accountName ?? t('navbar.accountSelector.selectAccount');

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {accountName !== null && (
        <Typography variant="body2" noWrap>
          {accountName}
        </Typography>
      )}

      {/* Sair da conta só faz sentido quando há uma conta ativa na sessão. */}
      {accountName !== null && (
        <Tooltip title={t('navbar.accountSelector.leaveAccount')}>
          <IconButton
            color="inherit"
            size="small"
            onClick={onLeaveAccount}
            aria-label={t('navbar.accountSelector.leaveAccount')}
          >
            <CloseIcon sx={{ fontSize: NAVBAR_ICON_SIZE }} />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title={triggerTitle}>
        <IconButton
          color="inherit"
          onClick={handleOpen}
          aria-label={t('navbar.accountSelector.selectAccount')}
          aria-haspopup="true"
          aria-expanded={isOpen}
        >
          {/* Glifo da fonte do legado (centernovo), o mesmo `centericon-database` da
              navbar Angular. O mapeamento vale dentro do slot da navbar; fora dele
              (Storybook, que não carrega o SCSS legado) o elemento fica vazio. */}
          <Box component="i" className="centericon-database" sx={{ fontSize: NAVBAR_ICON_SIZE }} />
        </IconButton>
      </Tooltip>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 1, width: 480 }}>
          {accountName !== null && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', px: 1, pb: 0.5 }}
            >
              {t('navbar.accountSelector.currentAccount', { name: accountName })}
            </Typography>
          )}
          <TextField
            size="small"
            fullWidth
            reserveHelperTextSpace={false}
            autoFocus
            placeholder={t('navbar.accountSelector.searchPlaceholder')}
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          />
          {/* Lista completa e rolável (teto de altura para não estourar a viewport); a busca refina o conteúdo. */}
          <MenuList dense sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
            {accounts.length === 0 && (
              <Typography variant="body2" sx={{ px: 2, py: 1 }}>
                {t('navbar.accountSelector.noAccountsFound')}
              </Typography>
            )}
            {accounts.map((account) => (
              <MenuItem
                key={account.schema ?? account.nomeConta}
                onClick={() => handleSelect(account)}
              >
                {account.nomeConta}
              </MenuItem>
            ))}
          </MenuList>
          {/* Sair só faz sentido quando há uma conta ativa na sessão. */}
          {accountName !== null && (
            <Button variant="text" size="small" onClick={handleLeave}>
              {t('navbar.accountSelector.leaveAccount')}
            </Button>
          )}
        </Box>
      </Popover>
    </Box>
  );
}
